/**
 * ContributionForm — the public "Help improve a connector" form (D.18 §2.3).
 *
 * Collects a connector contribution (displayName / category / documentationUrl /
 * manifest / notes + contributor handle) and POSTs it as JSON to the real
 * moderation route `POST /v1/library/contributions` (services/api … →
 * connector_library.promotion_staging, status='staged'). It NEVER publishes —
 * an admin reviews + cuts a versioned release. Holds STRUCTURE only.
 *
 * Shared by design (cross-surface parity): the same block backs the marketing
 * provider directory's "Help improve" CTA today and can back an in-platform
 * "contribute a connector" flow later — fed the right `endpoint` per surface.
 *
 * Air-gapped / sovereign degradation: the bundled marketing site has NO API, so
 * `endpoint` is unset. With no endpoint the form does not render its submit path
 * at all — it shows a "contact us" fallback instead (Rule 12: nothing reaches
 * for an API that isn't there). Auth: the live route is `customerScoped`, so a
 * real submit needs the caller's session cookie / key — `credentials: 'include'`
 * carries it on the SaaS site; a signed-out visitor gets the route's 401 surfaced
 * as the friendly "sign in / contact us" error (we never pretend it succeeded).
 *
 * Composes the shared Dialog + Input / Textarea / Label / Select / Button
 * primitives — no bespoke inline styling, design tokens only (Rule 33).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../dialog';
import { Input } from '../input';
import { Textarea } from '../textarea';
import { Label } from '../label';
import { Button } from '../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { trackEvent } from '../../analytics/track';

/** Connector categories a contributor can tag — mirrors the library's coarse buckets. */
const CATEGORIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'crm', label: 'CRM & Sales' },
  { value: 'finance', label: 'Finance & Accounting' },
  { value: 'payments', label: 'Payments' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'communication', label: 'Communication' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'hr', label: 'HR & People' },
  { value: 'storage', label: 'Storage & Files' },
  { value: 'devtools', label: 'DevTools & Infrastructure' },
  { value: 'analytics', label: 'Analytics & Data' },
  { value: 'other', label: 'Other' },
];

export interface ContributionFormProps {
  /** Whether the dialog is open (controlled by the host, e.g. the directory CTA). */
  readonly open: boolean;
  /** Open/close callback (Radix-style). */
  readonly onOpenChange: (open: boolean) => void;
  /**
   * Absolute URL of the live contribution route
   * (e.g. https://api.inferrex.com/v1/library/contributions). When UNSET the
   * form degrades to a contact-us fallback — the air-gapped / sovereign path
   * where there is no API to post to.
   */
  readonly endpoint?: string | undefined;
  /** Contact e-mail shown in the fallback + on error (single-sourced by the host). */
  readonly contactEmail?: string;
  /** Pre-fill the connector name (the directory passes the vendor being improved). */
  readonly initialDisplayName?: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

/** Slugify a display name to the route's providerKey shape (`^[a-z0-9][a-z0-9-]*$`). */
function toProviderKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function ContributionForm({
  open,
  onOpenChange,
  endpoint,
  contactEmail = 'hello@inferrex.com',
  initialDisplayName = '',
}: ContributionFormProps) {
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  // Re-seed the name + reset state whenever the host opens the dialog for a
  // (potentially different) vendor.
  useEffect(() => {
    if (open) {
      setDisplayName(initialDisplayName);
      setStatus('idle');
      setError('');
    }
  }, [open, initialDisplayName]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting' || !endpoint) return;
    const form = e.currentTarget;
    const data = new FormData(form);

    const name = String(data.get('displayName') ?? '').trim();
    const providerKey = toProviderKey(name);
    if (!name || !providerKey) {
      setStatus('error');
      setError('Please give the connector a name.');
      return;
    }

    // The manifest is optional free-text JSON; if provided it must parse (the
    // route requires a JSON object). Empty → send an empty object placeholder so
    // a docs-only contribution (just a documentation URL + notes) still lands.
    const manifestRaw = String(data.get('manifest') ?? '').trim();
    let manifest: unknown = {};
    if (manifestRaw) {
      try {
        manifest = JSON.parse(manifestRaw);
      } catch {
        setStatus('error');
        setError('The manifest must be valid JSON, or left blank.');
        return;
      }
      if (manifest === null || typeof manifest !== 'object' || Array.isArray(manifest)) {
        setStatus('error');
        setError('The manifest must be a JSON object describing the connector.');
        return;
      }
    }

    const body: Record<string, unknown> = {
      providerKey,
      displayName: name,
      manifest,
      ...(category ? { category } : {}),
    };
    const documentationUrl = String(data.get('documentationUrl') ?? '').trim();
    const notes = String(data.get('notes') ?? '').trim();
    const contributorName = String(data.get('contributorName') ?? '').trim();
    const contributorEmail = String(data.get('contributorEmail') ?? '').trim();
    if (documentationUrl) body.documentationUrl = documentationUrl;
    if (notes) body.notes = notes;
    if (contributorName) body.contributorName = contributorName;
    if (contributorEmail) body.contributorEmail = contributorEmail;

    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(
          res.status === 401 || res.status === 403
            ? 'Please sign in to contribute a connector.'
            : res.status === 429
              ? 'Too many submissions — please try again shortly.'
              : 'Something went wrong submitting your contribution.',
        );
      }
      setStatus('success');
      trackEvent('connector_contribution', { providerKey });
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  const field = 'space-y-1.5';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Help improve the connector library</DialogTitle>
          <DialogDescription>
            Submit a connector or an improvement. We review every submission before it&apos;s published — nothing goes live automatically.
          </DialogDescription>
        </DialogHeader>

        {!endpoint ? (
          // Air-gapped / sovereign: no API to post to — point them at a side channel.
          <div className="rounded-(--card-radius) border border-border bg-section-secondary p-(--space-card) text-center">
            <p className="text-muted-foreground">
              This deployment runs without the public contribution API. To suggest a connector improvement, email{' '}
              <a href={`mailto:${contactEmail}`} className="text-primary underline-offset-2 hover:underline">{contactEmail}</a>.
            </p>
          </div>
        ) : status === 'success' ? (
          <div className="rounded-(--card-radius) border border-border bg-section-secondary p-(--space-card) text-center">
            <p className="font-display text-xl font-semibold text-foreground">Thanks — we&apos;ve got it.</p>
            <p className="mt-2 text-muted-foreground">
              Your contribution is in the review queue. An admin will assess it before it&apos;s published.
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-(--space-card)" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-(--space-grid)" noValidate>
            <div className="grid gap-(--space-grid) sm:grid-cols-2">
              <div className={field}>
                <Label htmlFor="contrib-name">Connector name</Label>
                <Input
                  id="contrib-name"
                  name="displayName"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Acme Billing"
                  maxLength={255}
                />
              </div>
              <div className={field}>
                <Label htmlFor="contrib-category">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="contrib-category"><SelectValue placeholder="Choose one" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className={field}>
              <Label htmlFor="contrib-docs">Documentation URL <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="contrib-docs" name="documentationUrl" type="url" placeholder="https://docs.example.com/api" maxLength={2048} />
            </div>
            <div className={field}>
              <Label htmlFor="contrib-manifest">
                Connector manifest (JSON) <span className="text-muted-foreground">(optional — paste the structure if you have it)</span>
              </Label>
              <Textarea
                id="contrib-manifest"
                name="manifest"
                rows={5}
                placeholder={'{\n  "apis": [{ "apiType": "openapi", "baseUrl": "https://api.example.com" }],\n  "entities": []\n}'}
                className="font-mono text-xs"
                maxLength={50_000}
              />
            </div>
            <div className={field}>
              <Label htmlFor="contrib-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea id="contrib-notes" name="notes" rows={3} placeholder="What's missing or wrong, and what you'd improve." maxLength={2000} />
            </div>
            <div className="grid gap-(--space-grid) sm:grid-cols-2">
              <div className={field}>
                <Label htmlFor="contrib-by">Your name <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="contrib-by" name="contributorName" autoComplete="name" placeholder="So we can credit you" maxLength={255} />
              </div>
              <div className={field}>
                <Label htmlFor="contrib-email">Your email <span className="text-muted-foreground">(optional)</span></Label>
                <Input id="contrib-email" name="contributorEmail" type="email" autoComplete="email" placeholder="you@company.com" maxLength={320} />
              </div>
            </div>
            {status === 'error' && (
              <p className="text-sm text-destructive">
                {error} You can also email{' '}
                <a href={`mailto:${contactEmail}`} className="underline underline-offset-2">{contactEmail}</a>.
              </p>
            )}
            <Button type="submit" size="lg" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Submitting…' : 'Submit for review'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
